import * as yup from "yup";
import { validateFileSize } from "../../utils";
import {
  LoanProductType,
  MediaPurpose,
  MediaType,
} from "../../hooks/api/types";
import { LoanCalculatorState } from "../../contexts/LoanCalculator/types";
import { UploadMediaArgs } from "../../hooks/useMedia";
import { LoanApplicationContextValues } from "../../contexts";
import { CustomFile } from "../LoanApplicationSteps/FinancialInformation/service";
import {
  getDropzoneAcceptForPurpose,
  ACCEPT_ALL_DOCUMENTS_OBJ,
} from "../../utils/accept.util";

export type AdvanceCashflowLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type BuyNowPayLaterLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type RevenueBasedFinanceLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type POSRevenueBasedFinanceLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type ReceivablesFinanceLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
  discountRate: number;
};

export type PayablesFinanceLoanCalculatorFields = {
  loanAmount: number;
  duration: number;
};

export type LineOfCreditLoanCalculatorFields = {
  loanAmount: number;
};

export type UploadInvoiceFields = {
  invoices: File[];
  invoiceNo?: string;
  invoiceAmount?: number;
  invoiceIssuedDate?: Date;
  invoiceDueDate?: Date;
  vendorName: string;
};

export type Invoice = {
  invoiceNumber?: string;
  invoiceAmount?: number;
  requiredAmount?: number;
  issuedDate?: string;
  dueDate?: string;
};

export type DrawdownFields = {
  invoiceFiles: File[];
  vendorId: string;
  invoices: Invoice[];
  currency: string;
  totalAmount: number;
  numberOfInvoices: number;
  // AED conversion fields
  requestedAmountInAED?: number; // Tentative AED equivalent of requested amount
};

export type UploadStatementsFields = {
  salesStatements: File[];
};

export type UploadPOSStatementsFields = {
  posSalesStatements: File[];
};

export type UploadSalesInvoiceFields = {
  salesInvoices: File[];
};

type AdvanceCashflowLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

type BuyNowPayLaterLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

type RevenueBasedFinanceLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
  validateDuration: boolean;
};

type POSRevenueBasedFinanceLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

type ReceivablesFinanceLoanValidationSchemaArgs =
  AdvanceCashflowLoanValidationSchemaArgs & {
    discount: {
      min: number;
      max: number;
    };
  };

type PayablesFinanceLoanValidationSchemaArgs = {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
  duration: {
    min: number;
    max: number;
  };
};

export const LINE_OF_CREDIT_INITIAL_VALUES: LineOfCreditLoanCalculatorFields = {
  loanAmount: 100_000,
};

export const LOAN_ADVANCE_CASHFLOW_INITIAL_VALUES: AdvanceCashflowLoanCalculatorFields =
  {
    loanAmount: 50_000,
    duration: 6,
  };

export const LOAN_RECEIVABLES_FINANCE_INITIAL_VALUES: ReceivablesFinanceLoanCalculatorFields =
  {
    loanAmount: 50_000,
    duration: 6,
    discountRate: 50,
  };

export const LOAN_UPLOAD_INVOICE_INITIAL_VALUES: UploadInvoiceFields = {
  invoices: [],
  vendorName: "",
  invoiceNo: "",
  invoiceAmount: 0,
  invoiceIssuedDate: undefined,
  invoiceDueDate: undefined,
};

export const LOAN_UPLOAD_STATEMENTS_INITIAL_VALUES: UploadStatementsFields = {
  salesStatements: [],
};

export const LOAN_UPLOAD_POS_STATEMENTS_INITIAL_VALUES: UploadPOSStatementsFields =
  {
    posSalesStatements: [],
  };

export const LOAN_UPLOAD_SALES_INVOICES_INITIAL_VALUES: UploadSalesInvoiceFields =
  {
    salesInvoices: [],
  };

const ALLOWED_DOC_MIME_TYPES: string[] = Object.keys(ACCEPT_ALL_DOCUMENTS_OBJ);
const ALLOWED_DOC_EXTENSIONS: string[] = Object.values(
  ACCEPT_ALL_DOCUMENTS_OBJ
).flat();

function isAllowedDocumentFile(file: any): boolean {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  if (type && ALLOWED_DOC_MIME_TYPES.includes(type)) return true;
  return ALLOWED_DOC_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export const getAdvanceCashflowLoanValidationSchema = ({
  loanAmount,
  duration,
}: AdvanceCashflowLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const getBuyNowPayLaterLoanValidationSchema = ({
  loanAmount,
  duration,
}: BuyNowPayLaterLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const getRevenueBasedFinanceLoanValidationSchema = ({
  loanAmount,
  duration,
  validateDuration = true,
}: RevenueBasedFinanceLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min?.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max?.toLocaleString()}`
      )
      .required("Loan amount is required"),
    ...(validateDuration && {
      duration: yup.number().min(duration.min).max(duration.max),
    }),
  });
};

export const getLineOfCreditLoanValidationSchema = ({
  loanAmount,
}: {
  loanAmount: {
    min: number;
    max: number;
    unit: string;
  };
}) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min?.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max?.toLocaleString()}`
      )
      .required("Loan amount is required"),
  });
};

// Aliases for backward compatibility
export const getReceivablesLOCLoanValidationSchema =
  getLineOfCreditLoanValidationSchema;
export const getPayablesLOCLoanValidationSchema =
  getLineOfCreditLoanValidationSchema;

export const getPOSRevenueBasedFinanceLoanValidationSchema = ({
  loanAmount,
  duration,
}: POSRevenueBasedFinanceLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Amount cannot be less than ${
          loanAmount.unit
        } ${loanAmount.min?.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Amount cannot exceed than ${
          loanAmount.unit
        } ${loanAmount.max?.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const getReceivablesFinanceLoanValidationSchema = ({
  loanAmount,
  duration,
  discount,
}: ReceivablesFinanceLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Loan amount must be greater than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Loan amount cannot be higher than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
    //inclusive min and max
    discountRate: yup
      .number()
      .min(discount.min, (x) => `Borrow % should be greater than ${x.min}`)
      .max(discount.max, (x) => `Borrow % should be less than ${x.max}`),
  });
};

export const getPayablesFinanceLoanValidationSchema = ({
  loanAmount,
  duration,
}: PayablesFinanceLoanValidationSchemaArgs) => {
  return yup.object().shape({
    loanAmount: yup
      .number()
      .min(
        loanAmount.min,
        `Loan amount must be greater than ${
          loanAmount.unit
        } ${loanAmount.min.toLocaleString()}`
      )
      .max(
        loanAmount.max,
        `Loan amount cannot be higher than ${
          loanAmount.unit
        } ${loanAmount.max.toLocaleString()}`
      )
      .required("Loan amount is required"),
    duration: yup.number().min(duration.min).max(duration.max),
  });
};

export const SALES_STATEMENT_CONFIG = {
  accept: getDropzoneAcceptForPurpose(MediaPurpose.SALES_STATEMENT),
  maxFiles: 10,
  minFiles: 1,
  maxSize: 100 * 1024 * 1024,
};

export const POS_SALES_STATEMENT_CONFIG = {
  accept: getDropzoneAcceptForPurpose(MediaPurpose.POS_SALES_STATEMENT),
  maxFiles: 10,
  minFiles: 1,
  maxSize: 100 * 1024 * 1024,
};

export const SALES_INVOICE_CONFIG = {
  accept: getDropzoneAcceptForPurpose(MediaPurpose.SALES_INVOICE),
  maxFiles: 1,
  minFiles: 1,
  maxSize: 100 * 1024 * 1024,
};

export const LOAN_UPLOAD_SALES_STATEMENT_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    salesStatements: yup
      .array()
      .of(
        yup
          .mixed()
          .test(
            "fileType",
            "Please provide a supported document file",
            (value: any) => isAllowedDocumentFile(value)
          )
          .test("fileSize", "File is larger than 5 MB", validateFileSize(5))
      )
      .min(SALES_STATEMENT_CONFIG.minFiles)
      .max(SALES_STATEMENT_CONFIG.maxFiles)
      .required("Statement is required"),
  });

export const LOAN_UPLOAD_POS_SALES_STATEMENT_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    posSalesStatements: yup
      .array()
      .of(
        yup
          .mixed()
          .test(
            "fileType",
            "Please provide a supported document file",
            (value: any) => isAllowedDocumentFile(value)
          )
          .test("fileSize", "File is larger than 5 MB", validateFileSize(5))
      )
      .min(POS_SALES_STATEMENT_CONFIG.minFiles)
      .max(POS_SALES_STATEMENT_CONFIG.maxFiles)
      .required("Statement is required"),
  });

export const LOAN_UPLOAD_SALES_INVOICE_VALIDATION_SCHEMA = yup.object().shape({
  salesInvoices: yup
    .array()
    .of(
      yup
        .mixed()
        .test(
          "fileType",
          "Please provide a supported document file",
          (value: any) => isAllowedDocumentFile(value)
        )
        .test("fileSize", "File is larger than 5 MB", validateFileSize(5))
    )
    .min(SALES_INVOICE_CONFIG.minFiles)
    .max(SALES_INVOICE_CONFIG.maxFiles)
    .required("Invoice is required"),
});

export const LOAN_UPLOAD_INVOICE_VALIDATION_SCHEMA = yup.object().shape({
  invoices: yup
    .array()
    .min(1)
    .max(1)
    .of(yup.mixed().required())
    .required("Please upload an invoice to continue"),
  vendorName: yup
    .string()
    .required(
      "Please enter the name of the company which the invoice attached."
    ),
  invoiceNo: yup.string().required("Please enter invoice number."),
  invoiceAmount: yup.number().required("Please enter the invoice amount."),
  invoiceIssuedDate: yup
    .date()
    .required("Please enter the invoice issue date.")
    .max(new Date(), "Invoice issue date cannot be in the future.")
    .test("is-valid-date", "Please enter a valid date.", (value) => {
      const day = String(value.getDate()).padStart(2, "0");
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const year = value.getFullYear();
      const dateStr = `${day}/${month}/${year}`;
      return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || "");
    }),
  invoiceDueDate: yup
    .date()
    .required("Please enter the invoice due date.")
    .min(
      yup.ref("invoiceIssuedDate"),
      "Invoice due date must be later than the invoice issue date."
    )
    .test("is-valid-date", "Please enter a valid date.", (value) => {
      const day = String(value.getDate()).padStart(2, "0");
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const year = value.getFullYear();
      const dateStr = `${day}/${month}/${year}`;
      return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || "");
    }),
});

export const LOAN_UPDATE_INVOICE_DETAILS_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    invoiceNo: yup.string().required("Please enter invoice number."),
    invoiceIssuedDate: yup
      .date()
      .required("Please enter the invoice issue date.")
      .max(new Date(), "Invoice issue date cannot be in the future.")
      .test("is-valid-date", "Please enter a valid date.", (value) => {
        const day = String(value.getDate()).padStart(2, "0");
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const year = value.getFullYear();
        const dateStr = `${day}/${month}/${year}`;
        return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || "");
      }),
    invoiceDueDate: yup
      .date()
      .required("Please enter the invoice due date.")
      .min(
        yup.ref("invoiceIssuedDate"),
        "Invoice due date must be later than the invoice issue date."
      )
      .test("is-valid-date", "Please enter a valid date.", (value) => {
        const day = String(value.getDate()).padStart(2, "0");
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const year = value.getFullYear();
        const dateStr = `${day}/${month}/${year}`;
        return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || "");
      }),
  });

export type GetDrawdownValidationSchemaOptions = {
  vendorType: string;
  /** Facility available and, when known, capped by vendor headroom. */
  availableLimit: number;
  /**
   * Optional live reader so Formik can keep one schema instance while the
   * effective (facility ∩ vendor) limit changes.
   */
  getAvailableLimit?: () => number;
  requireCurrency?: boolean;
  maxDiscountLimit?: number;
  isPayablesFacility?: boolean;
  maxInvoices?: number;
};

/** Prefer LMS headroom; else sub-limit − utilisation when both are present. */
export const resolveVendorHeadroom = (
  vendor?: {
    headroom?: number | null;
    outstandingAmount?: number | null;
    subLimitAmount?: number | null;
  } | null
): number | null => {
  if (!vendor) return null;
  if (vendor.headroom != null) return vendor.headroom;
  if (vendor.outstandingAmount != null && vendor.subLimitAmount != null) {
    return vendor.subLimitAmount - vendor.outstandingAmount;
  }
  return null;
};

export const resolveEffectiveDrawdownLimit = (
  facilityAvailableLimit: number,
  vendorHeadroom: number | null
): number => {
  if (vendorHeadroom == null) return facilityAvailableLimit;
  return Math.min(facilityAvailableLimit, Math.max(0, vendorHeadroom));
};

export const getDrawdownValidationSchema = (
  options: GetDrawdownValidationSchemaOptions
) => {
  const {
    vendorType,
    availableLimit,
    getAvailableLimit,
    requireCurrency = true,
    maxDiscountLimit = 100,
    isPayablesFacility = false,
    maxInvoices,
  } = options;

  const readAvailableLimit = () =>
    typeof getAvailableLimit === "function"
      ? getAvailableLimit()
      : availableLimit;

  return yup.object({
    vendorId: yup.string().required(`${vendorType} is required`),
    currency: requireCurrency
      ? yup.string().required("Currency is required")
      : yup
          .string()
          .transform((value, originalValue) =>
            originalValue === "" ? undefined : value
          )
          .nullable()
          .notRequired(),
    invoiceFiles: yup
      .array()
      .min(1, "At least one invoice file is required")
      .max(10, "At most 10 files are allowed")
      .required("Invoice files are required"),
    invoices: (() => {
      let invoicesSchema = yup
        .array()
        .min(1, "At least one invoice is required");
      if (typeof maxInvoices === "number") {
        invoicesSchema = invoicesSchema.max(
          maxInvoices,
          `At most ${maxInvoices} invoices are allowed`
        );
      }
      return invoicesSchema.of(
        yup.object().shape({
          invoiceNumber: yup
            .string()
            .required("Invoice number is required")
            .test(
              "unique-invoice-number",
              "Invoice no already exists!",
              function (value, context) {
                if (!value) return true;

                const invoices = this.options.context?.invoices;
                if (!invoices || invoices.length <= 1) return true;

                const currentIndex = context.path.split("[")[1].split("]")[0];
                const isUnique = !invoices.some((invoice: any, index: any) => {
                  return (
                    index !== parseInt(currentIndex) &&
                    invoice.invoiceNumber === value
                  );
                });

                return isUnique;
              }
            ),
          invoiceAmount: yup
            .string()
            .matches(
              /^\d+(\.\d{1,2})?$/,
              "Invoice amount cannot have more than 2 decimal places"
            )
            .required("Invoice amount is required")
            .test(
              "invoiceAmount-gt-zero",
              "Invoice amount must be greater than 0",
              (value: string) => {
                if (!value) return false;
                const amount = parseFloat(value);
                return amount > 0;
              }
            ),
          requiredAmount: yup
            .string()
            .matches(
              /^\d+(\.\d{1,2})?$/,
              "Required amount cannot have more than 2 decimal places"
            )
            .required("Required amount is required")
            .test(
              "requiredAmount-lte",
              "Required amount exceeds available limit",
              (value) => {
                const requiredAmount = parseFloat(value);
                return requiredAmount <= readAvailableLimit();
              }
            )
            .test(
              "requiredAmount-gt-zero",
              "Required amount must be greater than 0",
              (value: string) => {
                if (!value) return false;
                const amount = parseFloat(value);
                return amount > 0;
              }
            )
            .test(
              "requiredAmount-lte-invoice",
              isPayablesFacility
                ? "Required amount cannot exceed invoice amount"
                : `Required amount cannot exceed ${
                    maxDiscountLimit ?? 100
                  }% of invoice amount`,
              function (value) {
                const requiredAmount = parseFloat(value);
                const invoiceAmount = parseFloat(this.parent.invoiceAmount);
                if (isPayablesFacility) {
                  return requiredAmount <= invoiceAmount;
                }
                const limit =
                  typeof maxDiscountLimit === "number" &&
                  Number.isFinite(maxDiscountLimit)
                    ? maxDiscountLimit
                    : 100;
                return requiredAmount <= (limit / 100) * invoiceAmount;
              }
            ),
          issuedDate: yup
            .date()
            .required("Invoice issue date is required.")
            .max(new Date(), "Invoice issue date cannot be in the future.")
            .test("is-valid-date", "Please enter a valid date.", (value) => {
              const day = String(value.getDate()).padStart(2, "0");
              const month = String(value.getMonth() + 1).padStart(2, "0");
              const year = value.getFullYear();
              const dateStr = `${day}/${month}/${year}`;
              return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || "");
            }),
          dueDate: yup
            .date()
            .required("Invoice due date is required.")
            .min(
              yup.ref("issuedDate"),
              "Invoice due date must be later than the invoice issue date."
            )
            .test("is-valid-date", "Please enter a valid date.", (value) => {
              const day = String(value.getDate()).padStart(2, "0");
              const month = String(value.getMonth() + 1).padStart(2, "0");
              const year = value.getFullYear();
              const dateStr = `${day}/${month}/${year}`;
              return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || "");
            })
            .test(
              "is-future-date",
              "Invoice due date cannot be in the past.",
              (value) => {
                if (!value) {
                  return true;
                }
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const dueDate = new Date(value);
                dueDate.setHours(0, 0, 0, 0);
                return dueDate >= today;
              }
            ),
        })
      );
    })(),
    totalAmount: yup.number().test("total-amount-limit", function (_value) {
      const invoices = this.options.context?.invoices;
      if (!invoices || invoices.length <= 1) return true;
      const total = invoices.reduce(
        (sum: number, invoice: any) =>
          sum + Number(invoice.requiredAmount || 0),
        0
      );
      const limit = readAvailableLimit();
      if (total <= limit) return true;
      return this.createError({
        message: `Total drawdown amount cannot exceed available limit of ${limit}`,
      });
    }),

    numberOfInvoices: yup
      .number()
      .test("complete-invoices-count", function (_value) {
        const invoices = this.options.context?.invoices;
        if (!invoices || !Array.isArray(invoices)) {
          return this.createError({ message: "No invoices provided" });
        }
        const completeCount = invoices.filter((invoice: any) =>
          Object.values(invoice).every(
            (v) => v !== "" && v !== null && v !== undefined
          )
        ).length;

        if (completeCount < 1) {
          return this.createError({
            message: "At least one complete invoice is required",
          });
        }
        if (typeof maxInvoices === "number" && completeCount > maxInvoices) {
          return this.createError({
            message: `Maximum ${maxInvoices} invoices are allowed`,
          });
        }
        return true;
      }),
  });
};

export async function handleReceivablesFinanceLoanUpdate(
  loanApplicationId: string,
  invoiceFiles: File[],
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>,
  upload: (payload: UploadMediaArgs) => Promise<string>,
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>
) {
  setLoading && setLoading(true);
  const invoicePromises = invoiceFiles.map((file) =>
    upload({
      file,
      type: MediaType.DOCUMENT,
      purpose: MediaPurpose.INVOICE,
      loanApplicationId: loanApplicationId,
    })
  );
  const invoiceIds = await Promise.all(invoicePromises);
  if (invoiceIds.length) {
    const invoices = calculatorState.invoiceDiscountingLoan.invoices.map(
      (invoice, index) => ({
        ...invoice,
        invoiceId: invoiceIds[index],
        discountRate: invoice.discountRate,
        ...(invoice.invoiceDueDate && {
          invoiceDueDate: invoice.invoiceDueDate,
        }),
      })
    );
    await update({
      id: loanApplicationId,
      loanType: LoanProductType.INVOICE_DISCOUNTING,
      invoices,
    });
  }
  setLoading && setLoading(false);
}

export async function handlePayablesFinanceLoanUpdate(
  loanApplicationId: string,
  invoiceFiles: File[],
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>,
  upload: (payload: UploadMediaArgs) => Promise<string>,
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>
) {
  setLoading && setLoading(true);
  const invoicePromises = invoiceFiles.map((file) =>
    upload({
      file,
      type: MediaType.DOCUMENT,
      purpose: MediaPurpose.INVOICE,
      loanApplicationId: loanApplicationId,
    })
  );
  const invoiceIds = await Promise.all(invoicePromises);
  if (invoiceIds.length) {
    const invoices = calculatorState.payableFinancingLoan.invoices.map(
      (invoice, index) => ({
        ...invoice,
        invoiceId: invoiceIds[index],
        discountRate: invoice.discountRate,
        ...(invoice.invoiceDueDate && {
          invoiceDueDate: invoice.invoiceDueDate,
        }),
      })
    );
    await update({
      id: loanApplicationId,
      loanType: LoanProductType.PAYABLE_FINANCING,
      invoices,
    });
  }
  setLoading && setLoading(false);
}

export async function handleRevenueBasedFinanceLoanUpdate(
  loanApplicationId: string,
  salesStatementFiles: File[],
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>,
  upload: (payload: UploadMediaArgs) => Promise<string>
) {
  const salesStatementPromises = salesStatementFiles.map((file) =>
    upload({
      file,
      type: MediaType.DOCUMENT,
      purpose: MediaPurpose.SALES_STATEMENT,
      loanApplicationId: loanApplicationId,
    })
  );
  const salesStatementIds = await Promise.all(salesStatementPromises);
  const salesStatements = salesStatementIds.map((_statement, index) => ({
    documentId: salesStatementIds[index],
  }));
  await update({
    id: loanApplicationId,
    loanType: LoanProductType.REVENUE_BASED_FINANCING,
    revenueBasedLoan: {
      ...calculatorState.revenueBasedLoan,
      salesStatements: salesStatements,
    },
  });
}

export async function handlePosRevenueBasedFinanceLoanUpdate(
  loanApplicationId: string,
  posSalesStatementFiles: File[],
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>,
  upload: (payload: UploadMediaArgs) => Promise<string>
) {
  const posSalesStatementPromises = posSalesStatementFiles.map((file) =>
    upload({
      file,
      type: MediaType.DOCUMENT,
      purpose: MediaPurpose.POS_SALES_STATEMENT,
      loanApplicationId: loanApplicationId,
    })
  );
  const posSalesStatementIds = await Promise.all(posSalesStatementPromises);
  const posSalesStatements = posSalesStatementIds.map((_statement, index) => ({
    posStatementId: posSalesStatementIds[index],
  }));
  await update({
    id: loanApplicationId,
    loanType: LoanProductType.POS_REVENUE_BASED_FINANCING,
    posRevenueBasedLoan: {
      ...calculatorState.posRevenueBasedLoan,
      posSalesStatements: posSalesStatements,
    },
  });
}

export async function handleBuyNowPayLaterLoanUpdate(
  loanApplicationId: string,
  salesInvoiceFiles: File[],
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>,
  upload: (payload: UploadMediaArgs) => Promise<string>
) {
  const salesInvoicePromises = salesInvoiceFiles.map((file) =>
    upload({
      file,
      type: MediaType.DOCUMENT,
      purpose: MediaPurpose.INVOICE,
      loanApplicationId: loanApplicationId,
    })
  );
  const salesInvoiceIds = await Promise.all(salesInvoicePromises);
  const salesInvoices = salesInvoiceIds.map((_invoice, index) => ({
    invoiceId: salesInvoiceIds[index],
  }));
  await update({
    id: loanApplicationId,
    loanType: LoanProductType.BNPL,
    buyNowPayLaterLoan: {
      ...calculatorState.buyNowPayLaterLoan,
      salesInvoices: [...salesInvoices],
    },
  });
}

export async function handleAdvanceCashflowLoanUpdate(
  loanApplicationId: string,
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>
) {
  await update({
    id: loanApplicationId,
    loanType: LoanProductType.FIXED_TERM,
    fixedTermLoan: calculatorState.fixedTermLoan,
  });
}

type LoanDocumentFiles = {
  invoiceFiles: File[];
  salesStatementFiles: File[];
  salesInvoiceFiles: File[];
  posSalesStatementFiles: File[];
};

export const updateLoansStateForLegacyFlow = async (
  loanApplicationId: string,
  calculatorState: LoanCalculatorState,
  update: (
    payload: Partial<LoanApplicationContextValues["state"]>,
    options?: { local?: boolean }
  ) => Promise<any>,
  upload: (payload: UploadMediaArgs) => Promise<string>,
  files: LoanDocumentFiles
) => {
  switch (calculatorState.type) {
    case LoanProductType.FIXED_TERM:
      await handleAdvanceCashflowLoanUpdate(
        loanApplicationId,
        calculatorState,
        update
      );
      break;
    case LoanProductType.INVOICE_DISCOUNTING:
      await handleReceivablesFinanceLoanUpdate(
        loanApplicationId,
        files.invoiceFiles,
        calculatorState,
        update,
        upload
      );
      break;
    case LoanProductType.PAYABLE_FINANCING:
      await handlePayablesFinanceLoanUpdate(
        loanApplicationId,
        files.invoiceFiles,
        calculatorState,
        update,
        upload
      );
      break;
    case LoanProductType.REVENUE_BASED_FINANCING:
      await handleRevenueBasedFinanceLoanUpdate(
        loanApplicationId,
        files.salesStatementFiles,
        calculatorState,
        update,
        upload
      );
      break;
    case LoanProductType.BNPL:
      await handleBuyNowPayLaterLoanUpdate(
        loanApplicationId,
        files.salesInvoiceFiles,
        calculatorState,
        update,
        upload
      );
      break;
    case LoanProductType.POS_REVENUE_BASED_FINANCING:
      await handlePosRevenueBasedFinanceLoanUpdate(
        loanApplicationId,
        files.posSalesStatementFiles,
        calculatorState,
        update,
        upload
      );
      break;
    default:
      throw new Error("Invalid loan type");
  }
};

export const getMediaFromStatementIds = async (
  statementIds: string[],
  get: (args: { id: string }) => Promise<any>,
  onComplete: (retrievedMedia: CustomFile[]) => void
) => {
  const retrievedMedia: CustomFile[] = [];
  const promises = statementIds.map((statementId) =>
    get({ id: statementId })
      .then((res) => {
        const file = new CustomFile(
          new File([], res.media.fileName),
          res.media.id
        );
        Object.defineProperty(file, "size", {
          value: res.media.size,
        });
        retrievedMedia.push(file);
      })
      .catch((err) => {
        console.log(err);
      })
  );

  await Promise.all(promises);
  onComplete(retrievedMedia);
};
